import React, { useEffect, useState } from 'react'
import Menu from '@mui/material/Menu'
import { Calendar } from 'react-date-range'
import format from 'date-fns/format'
import isNull from '../../utils/NullChecking'
import ReactInputMask from 'react-input-mask'
import { useSelector } from "react-redux";

const DatePicker = (props) => {
    const {
        className,
        handleCalendarChangefunc,
        label,
        showLabel = true,
        maxDate,
        minDate,
        value,
        inputError,
        isReadOnly,
        blockedDates,
    } = { ...props };

    let DropdownData = useSelector(state => state.GeneralDropDownsReducer);
    const currentDate = DropdownData?.CurrentPracticeDate;

    // ✅ Convert Redux date → JS Date
    const getReferenceDate = () => {
        if (currentDate) {
            const [month, day, year] = currentDate.split('/');
            return new Date(year, month - 1, day);
        }
        return new Date();
    };

    const referenceDate = getReferenceDate();

    const [date, setDate] = useState(value || '');
    const [calendarDate, setCalendarDate] = useState(referenceDate);
    const [position, setPosition] = useState(null);
    const [placeholder, setPlaceholder] = useState(false);

    const openRangePicker = Boolean(position);

    const minSlctdDate = new Date(new Date().getFullYear() - 150, new Date().getMonth(), new Date().getDate());
    const maxSlctdDate = new Date(new Date().getFullYear() + 20, new Date().getMonth(), new Date().getDate());

    // ✅ Compare dates
    const isSameDay = (d1, d2) => {
        return d1?.getDate() === d2?.getDate() &&
            d1?.getMonth() === d2?.getMonth() &&
            d1?.getFullYear() === d2?.getFullYear();
    };

    const handleCustomDate = (e) => {
        setDate(e.target.value);
    };

    const handleBlur = () => {
        if (!date) {
            handleCalendarChangefunc("", hide());
            return;
        }

        const onlyDigits = date.replace(/\D/g, "");
        if (onlyDigits.length !== 8) {
            setDate("");
            handleCalendarChangefunc("", hide());
            return;
        }

        const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
        const matches = date.match(regex);

        if (matches) {
            const [, mm, dd, yyyy] = matches;
            const parsedDate = new Date(yyyy, mm - 1, dd);

            if (
                parsedDate.getMonth() + 1 !== parseInt(mm) ||
                parsedDate.getDate() !== parseInt(dd)
            ) {
                setDate("");
                handleCalendarChangefunc("", hide());
                return;
            }

            if (parsedDate < minSlctdDate) {
                commitDate(minSlctdDate);
            } else if (parsedDate > maxSlctdDate) {
                commitDate(maxSlctdDate);
            } else {
                commitDate(parsedDate);
            }
        } else {
            setDate("");
            handleCalendarChangefunc("", hide());
        }
    };

    const commitDate = (d) => {
        const formatted = format(d, 'MM/dd/yyyy');
        setDate(formatted);
        setCalendarDate(d);
        handleCalendarChangefunc(formatted, hide());
    };

    const handleChangeCalendar = (d) => {
        commitDate(d);
    };

    // ✅ SMART OPEN LOGIC (FINAL FIX)
    const show = (event) => {
        if (date) {
            const parts = date.split('/');
            if (parts.length === 3) {
                const parsed = new Date(parts[2], parts[0] - 1, parts[1]);

                if (!isNaN(parsed)) {
                    setCalendarDate(parsed); // selected date
                } else {
                    setCalendarDate(referenceDate);
                }
            } else {
                setCalendarDate(referenceDate);
            }
        } else {
            setCalendarDate(referenceDate); // fallback
        }

        setPosition(event.currentTarget);
    };

    const hide = () => { setPosition(null) };

    useEffect(() => {
        if (!isNull(value)) {
            setDate(value);

            const parts = value.split('/');
            if (parts.length === 3) {
                const parsed = new Date(parts[2], parts[0] - 1, parts[1]);
                setCalendarDate(parsed);
            }
        } else {
            setDate('');
            setCalendarDate(referenceDate);
        }
    }, [value, currentDate]);

    const MinimumRate = new Date();
    MinimumRate.setFullYear(MinimumRate.getFullYear() - 150);

    return (
        <div className='ta-date-picker'>
            <div className={`input calender ${className || ''}`}>
                {showLabel && <label>{label}</label>}
                <div className='calender-icon '>
                    <ReactInputMask
                        mask="99/99/9999"
                        maskChar={null}
                        value={date}
                        onChange={handleCustomDate}
                        onBlur={handleBlur}
                        className={inputError ? 'form-control input_error mb-0' : 'form-control'}
                        placeholder={props.placeholder ? props.placeholder : placeholder ? 'MM/DD/YYYY' : ""}
                        autoComplete="off"
                        onClick={() => setPlaceholder(true)}
                        readOnly={isReadOnly}
                        disabled={isReadOnly}
                    />
                    <i className="icon-calender input-icon" onClick={show}>
                        <span className="path1"></span><span className="path2"></span>
                        <span className="path3"></span><span className="path4"></span>
                        <span className="path5"></span><span className="path6"></span>
                    </i>
                </div>

                {isReadOnly ? null : (
                    <Menu
                        className='calender-menu'
                        anchorEl={position}
                        open={openRangePicker}
                        onClose={hide}
                        MenuListProps={{ 'aria-labelledby': 'basic-button' }}
                    >
                        <Calendar
                            date={calendarDate}
                            maxDate={maxDate}
                            minDate={minDate ? minDate : MinimumRate}
                            onChange={handleChangeCalendar}
                            disabledDates={blockedDates}
                            weekdayDisplayFormat="EEEEE"

                            //  RedLine highlight for redux date today 
                            dayContentRenderer={(date) => {
                                const isToday = isSameDay(date, referenceDate);
                                return (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '100%',
                                        height: '100%',
                                    }}>
                                        <span style={{
                                            width: '28px',
                                            height: '28px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            ...(isToday && {
                                                fontWeight: 'bold',
                                                borderBottom: '2px solid red'
                                            })
                                        }}>
                                            {date.getDate()}
                                        </span>
                                    </div>
                                );
                            }}
                        />
                    </Menu>
                )}
            </div>
        </div>
    )
}

export default DatePicker;